<template>
  <div class="card-item card-item-attribute" v-ifield-b-tooltip="tooltipOption">
    <span
      class="text value"
      v-bind:class="{
        'ellipsis': needsToTruncate.value,
        'both': needsToTruncateBoth,
      }"
      v-bind:style="{ '--basis-chars': thresholdChars.value }"
    >
      {{ valueText }}
    </span>
    <span
      class="text attribute-name"
      v-bind:style="{ '--basis-chars': thresholdChars.name }"
    >
      <span
        class="text"
        v-bind:class="{
          'ellipsis': needsToTruncate.name,
          'both': needsToTruncateBoth,
        }"
      >
        {{ attributeName }}
      </span>
    </span>
    <!-- 幅計測用隠し要素 -->
    <span ref="value" class="hidden text value">{{ valueText }}</span>
    <span ref="name" class="hidden text attribute-name">
      <span class="text">{{ attributeName }}</span>
    </span>
  </div>
</template>

<script>
import { toRefs } from 'vue'
import { useUserStatusAdditionalItemUserAttribute } from '@/composables/user/additionalItem'
import VARIABLES from '@/assets/scss/variables.module.scss'

export default {
  name: 'AppCardMemberItemUserAttribute',
  inheritAttrs: false,
  props: {
    /**
     * 属性タイプ
     */
    attributeType: {
      type: String,
      default: null,
    },
    /**
     * 属性名
     */
    attributeName: {
      type: String,
      default: null,
    },
    /**
     * 属性値
     */
    value: {
      type: String,
      default: null,
    },
    /**
     * 1remのサイズ(px)
     */
    remSize: {
      type: Number,
      default: 16,
    },
  },
  setup(props) {
    const { attributeType, attributeName, value } = toRefs(props)
    const { valueText, text } = useUserStatusAdditionalItemUserAttribute({
      attributeType,
      attributeName,
      value,
    })
    return { valueText, text }
  },
  data: function () {
    return {
      // 要素幅
      width: {
        root: 0,
        value: 0,
        name: 0,
        gap: 4,
      },
      // 表示字数閾値
      thresholdChars: {
        value: 8, // 8文字
        name: 5, // 4文字 + 両括弧0.5*2文字
      },
    }
  },
  computed: {
    /**
     * 文字サイズ(px)
     * @type {Number}
     */
    fontSize: function () {
      const ratio = +VARIABLES.FONT_SIZE_SMALL.replace('rem', '')
      return this.remSize * ratio
    },
    /**
     * 省略が必要な場合はtrue
     * @type {Object}
     */
    needsToTruncate: function () {
      const { root: wRoot, gap: wGap, name: wName, value: wValue } = this.width

      // 全体から必ず除く分(名称左のgap)を引いた幅
      const wLeftRoot = wRoot - wGap

      // 省略しない値テキスト幅 が 全体 から 想定される名称部 を除いた幅に収まらない場合は長すぎる
      const wMinName = Math.min(wName, this.thresholdChars.name * this.fontSize)
      const isValueTooLong = wValue > wLeftRoot - wMinName

      // 省略しない名称テキスト幅 が 全体 から 想定される値部 を除いた幅に収まらない場合は長過ぎる
      const wMinValue = Math.min(
        wValue,
        this.thresholdChars.value * this.fontSize
      )
      const isNameTooLong = wName > wLeftRoot - wMinValue

      return {
        value: isValueTooLong,
        name: isNameTooLong,
      }
    },
    /**
     * 値と名称のどちらかに省略が必要な場合はtrue
     * @type {boolean}
     */
    needsToTruncateSome: function () {
      return [...Object.values(this.needsToTruncate)].some((v) => v)
    },
    /**
     * 値と名称の両方に省略が必要な場合はtrue
     * @type {boolean}
     */
    needsToTruncateBoth: function () {
      return [...Object.values(this.needsToTruncate)].every((v) => v)
    },
    /**
     * ツールチップオプション
     * @type {Object}
     */
    tooltipOption: function () {
      return {
        title: this.text,
        disabled: !this.needsToTruncateSome,
      }
    },
  },
  watch: {
    valueText: function () {
      this.$nextTick(() => {
        this.$_measureWidth('value')
      })
    },
    attributeName: function () {
      this.$nextTick(() => {
        this.$_measureWidth('name')
      })
    },
  },
  mounted: function () {
    this.observer = new ResizeObserver(() => this.$_measureWidth('root'))
    this.observer.observe(this.$el)
    this.$_measureWidth('value')
    this.$_measureWidth('name')
  },
  beforeDestroy: function () {
    this.observer.unobserve(this.$el)
  },
  methods: {
    /**
     * 要素幅を計測する
     * @param {string} key 対象要素のキー
     */
    $_measureWidth: function (key) {
      const target = key === 'root' ? this.$el : this.$refs[key]
      const rect = target?.getBoundingClientRect()
      this.width[key] = rect?.width || 0
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/member/card-member-item';

.value {
  --basis-chars: 8;

  // .ellipsisがないときは全文表示を保証する
  &:not(.ellipsis) {
    flex-shrink: 0;
  }

  // 値と名称の両方が省略されるときは
  // * 基本サイズとして--basis-chars文字分の幅を確保する
  // * 値:名称 が 2:1 でスペースを割り振られるようにする
  &.ellipsis.both {
    flex-grow: 2;
    flex-basis: calc(#{$font-size-small} * var(--basis-chars));
  }
}

.attribute-name {
  --basis-chars: 5;

  display: grid;
  grid-template-columns: min-content auto min-content;
  margin: 0 !important;

  .text {
    margin: 0;
  }
  &::before {
    content: '(';
  }
  &::after {
    content: ')';
  }

  // 子に.ellipsisがないときは全文表示を保証する
  &:not(:has(.ellipsis)) {
    flex-shrink: 0;
  }

  // 値と名称の両方が省略されるときは
  // * 基本サイズとして--basis-chars文字分の幅を確保する
  // * 値:名称 が 2:1 でスペースを割り振られるようにする
  &:has(.ellipsis.both) {
    flex-grow: 1;
    flex-basis: calc(#{$font-size-small} * var(--basis-chars));
  }
}
</style>
